import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  getSocialConfig, availableProviders, runtimePlatform, loadAppleJs, loadGoogleGis,
  makeNonce, appleWebSignIn, nativeSignIn, SocialCancelled, NONCE_REFRESH_MS,
} from '../lib/socialSignIn';

/**
 * B-53: "Continue with Apple" / "Continue with Google" buttons, used on Log
 * in, Register and Invite-accept. Draws nothing unless the server has the
 * provider switched on AND this device/host may use it (lib/socialSignIn.js).
 *
 *   onBeforeSignIn()          — called just before the session can change,
 *                               so a page can stop its own "already signed
 *                               in" redirect racing ours.
 *   onSignedIn(user, provider) — signed in (existing or newly linked account).
 *   onNeedsProfile(pending)    — someone new: show <SocialProfileStep/>.
 *
 * Branding: Apple's button is black with the Apple logo and "Continue with
 * Apple"; on the web Google's button is the one Google Identity Services
 * renders itself (required there); in the apps it's Google's standard light
 * button. Both buttons are the same size, Apple first (App Store 4.8 asks for
 * an equivalent option, not a lesser one).
 */
export const PROVIDER_NAME = { apple: 'Apple', google: 'Google' };

const AppleLogo = () => (
  <svg width="16" height="19" viewBox="0 0 814 1000" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M788 341c-6 4-108 62-108 190 0 149 131 202 135 203-1 3-21 72-69 142-43 62-88 124-156 124s-86-40-164-40c-77 0-104 41-166 41s-106-58-156-128C46 791 0 664 0 543c0-194 126-297 250-297 66 0 121 43 162 43 39 0 101-46 176-46 29 0 131 3 200 98zM554 159c31-37 53-88 53-139 0-7-1-14-2-20-50 2-110 34-146 76-28 32-55 83-55 135 0 8 1 16 2 18 3 1 9 2 14 2 45 0 102-30 134-72z" />
  </svg>
);

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.4 13.7 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 7l7.4 5.7c4.3-4 6.9-9.9 6.9-17.2z" />
    <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.5 0 20.1 0 24s1 7.5 2.6 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.3 0-11.6-4.2-13.5-10l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

// Google Identity Services' own button (web). Each sign-in gets a fresh nonce:
// after a credential arrives the button is re-armed with a new one.
const GoogleWebButton = ({ clientId, onCredential, onUnavailable }) => {
  const box = useRef(null);
  const nonceRef = useRef(null);
  const cb = useRef(onCredential);
  cb.current = onCredential;

  useEffect(() => {
    let live = true;
    let timer = null;
    // Each arm takes a fresh server nonce; re-armed after every credential
    // and before a held nonce can expire on a page left open.
    const arm = async () => {
      const g = window.google?.accounts?.id;
      if (!live || !g || !box.current) return;
      clearTimeout(timer);
      let n;
      try { n = await makeNonce(); } catch { if (live) onUnavailable(); return; }
      if (!live) return;
      timer = setTimeout(arm, NONCE_REFRESH_MS);
      nonceRef.current = n.raw;
      g.initialize({
        client_id: clientId,
        nonce: n.hashed,
        ux_mode: 'popup',
        auto_select: false,
        callback: (resp) => {
          const raw = nonceRef.current;
          arm();
          if (resp?.credential) cb.current({ provider: 'google', idToken: resp.credential, nonce: raw });
        },
      });
      const width = Math.round(Math.min(400, Math.max(200, box.current.offsetWidth || 320)));
      box.current.innerHTML = '';
      g.renderButton(box.current, {
        type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
        shape: 'rectangular', logo_alignment: 'center', width, locale: 'en-GB',
      });
    };
    loadGoogleGis().then(arm).catch(() => { if (live) onUnavailable(); });
    return () => { live = false; clearTimeout(timer); };
  // eslint-disable-next-line
  }, [clientId]);

  return <div ref={box} className="social-google-web" />;
};

// B-68: the staff portal passes only={['google']} and its own signIn (which
// sends staffOnly and keeps the portal's session state).
const SocialSignIn = ({ onBeforeSignIn, onSignedIn, onNeedsProfile, dividerText = 'or use your email', only, signIn }) => {
  const { socialSignIn: memberSignIn } = useAuth();
  const socialSignIn = signIn || memberSignIn;
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [googleWebDown, setGoogleWebDown] = useState(false);
  const web = runtimePlatform() === 'web';

  useEffect(() => {
    let live = true;
    getSocialConfig().then(c => { if (live) setCfg(c); });
    return () => { live = false; };
  }, []);

  const offered = availableProviders(cfg);
  const avail = only ? { apple: offered.apple && only.includes('apple'), google: offered.google && only.includes('google') } : offered;
  // Apple JS must already be loaded when the button is pressed: loading it
  // after the click would lose the click and the popup would be blocked.
  // Same for the nonce (security review: server-issued), so nothing awaits
  // between the click and Apple's popup.
  const appleNonce = useRef(null);
  const [appleNonceTick, setAppleNonceTick] = useState(0);
  useEffect(() => {
    if (!(web && avail.apple)) return undefined;
    let live = true;
    loadAppleJs().catch(() => {});
    makeNonce().then(n => { if (live) appleNonce.current = n; }).catch(() => { appleNonce.current = null; });
    const t = setTimeout(() => setAppleNonceTick(x => x + 1), NONCE_REFRESH_MS);
    return () => { live = false; clearTimeout(t); };
  }, [web, avail.apple, appleNonceTick]);

  const finish = async (credential) => {
    setError('');
    setBusy(true);
    try {
      if (onBeforeSignIn) onBeforeSignIn();
      const r = await socialSignIn(credential);
      // needsProfile (DOB + terms) or needsEmailCode (prove a non-Gmail
      // Google address): the page shows <SocialPendingStep/>.
      if (r.needsProfile || r.needsEmailCode) onNeedsProfile(r);
      else await onSignedIn(r.user, credential.provider);
    } catch (e) {
      setError(e?.response?.data?.error || `Signing in with ${PROVIDER_NAME[credential.provider]} didn't work. Please try again.`);
    } finally {
      setBusy(false);
    }
  };

  const start = async (provider) => {
    if (busy) return;
    setError('');
    let credential;
    try {
      // On the web only Apple comes through here (Google's web button is
      // GIS's own and calls finish() directly).
      if (web && provider === 'apple') {
        const ready = appleNonce.current;
        appleNonce.current = null; // single use
        setAppleNonceTick(x => x + 1); // fetch the next one
        credential = await appleWebSignIn(cfg, ready);
      } else {
        credential = await nativeSignIn(provider, cfg);
      }
    } catch (e) {
      if (!(e instanceof SocialCancelled)) setError(`Signing in with ${PROVIDER_NAME[provider]} didn't work. Please try again.`);
      return;
    }
    await finish(credential);
  };

  const showGoogle = avail.google && !(web && googleWebDown);
  if (!avail.apple && !showGoogle) return null;

  return (
    <div className="social-signin" aria-busy={busy}>
      <div className={`social-buttons${busy ? ' is-busy' : ''}`}>
        {avail.apple && (
          <button type="button" className="social-btn social-btn-apple" onClick={() => start('apple')} disabled={busy}>
            <AppleLogo /> Continue with Apple
          </button>
        )}
        {showGoogle && (web
          ? <GoogleWebButton clientId={cfg.google.webClientId} onCredential={finish} onUnavailable={() => setGoogleWebDown(true)} />
          : (
            <button type="button" className="social-btn social-btn-google" onClick={() => start('google')} disabled={busy}>
              <GoogleG /> Continue with Google
            </button>
          ))}
      </div>
      {busy && <p className="social-status">Signing you in…</p>}
      {error && <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>}
      <div className="social-divider"><span>{dividerText}</span></div>
    </div>
  );
};

export default SocialSignIn;
